import React, { useState, useEffect } from 'react';
import { useApi } from '../Api';
import ChatWindow from '../components/ChatWindow';
import { FormSelect } from '../controls/FormSelect';
import AuthRedirect from '../AuthRedirect';
import {
    UserCircleIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { useIsMobile } from '../hooks/isMobile';
import {
    formatTimestamp,
    formatRelativeTime,
    groupMessagesByDate,
    formatDateHeader,
} from '../utils/dateFormatting';
import { getResponsiveHeight } from '../utils/heightCalculations';
import { dagToast } from '../utils/glowUp';
import {
    Conversation,
    Message,
    Family,
    ConversationStatus,
    ConversationCategory,
} from '../types/Messaging';

const MessagingPage: React.FC = () => {
    const api = useApi();
    const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [showFamilyPicker, setShowFamilyPicker] = useState(false);
    const [families, setFamilies] = useState<Family[]>([]);
    const [selectedFamilyId, setSelectedFamilyId] = useState<number | "">("");
    const isMobile = useIsMobile();

    const selectedConv = conversations.find(conv => conv.id.toString() === selectedConversation) || null;

    // Fetch user id, conversations, and families on mount
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true);
                setErrorMessage(null);

                const userResponse = await api.get('/api/profile/GetLoginProfileId');
                setCurrentUserId(userResponse.data);

                const conversationsResponse = await api.get('/api/messaging/getclientconversations');
                const conversationsData: Conversation[] = (conversationsResponse.data.conversations || []).map((c: any) => ({
                    ...c,
                    status: typeof c.status === "string"
                        ? Number(ConversationStatus[c.status as keyof typeof ConversationStatus])
                        : c.status,
                    category: typeof c.category === "string"
                        ? Number(ConversationCategory[c.category as keyof typeof ConversationCategory])
                        : c.category,
                }));
                setConversations(conversationsData);
                const familiesResponse = await api.get('/api/clientfamily/getfamilies');
                setFamilies(familiesResponse.data.families || []);
            } catch (err) {
                console.error('Error fetching user data:', err);
                setErrorMessage('Failed to load user data');
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [api]);

    // Poll for conversations
    useEffect(() => {
        const pollForConversations = async () => {
            try {
                const response = await api.get('/api/messaging/getclientconversations');
                const conversationsData: Conversation[] = (response.data.conversations || []).map((c: any) => ({
                    ...c,
                    status: typeof c.status === "string"
                        ? Number(ConversationStatus[c.status as keyof typeof ConversationStatus])
                        : c.status,
                    category: typeof c.category === "string"
                        ? Number(ConversationCategory[c.category as keyof typeof ConversationCategory])
                        : c.category,
                }));
                setConversations(conversationsData);
            } catch (err) {
                console.error('Error polling for conversations:', err);
            }
        };
        const interval = setInterval(pollForConversations, 5000);
        return () => clearInterval(interval);
    }, [api]);

    // Fetch messages for selected conversation
    useEffect(() => {
        const fetchMessages = async (conversationId: string) => {
            try {
                setMessagesLoading(true);
                setErrorMessage(null);
                const response = await api.get(`/api/messaging/GetMessages?conversationId=${conversationId}`);
                setMessages(response.data.messages || []);
            } catch (err) {
                console.error('Error fetching messages:', err);
                setErrorMessage('Failed to load messages');
            } finally {
                setMessagesLoading(false);
            }
        };
        if (selectedConversation) {
            fetchMessages(selectedConversation);
        }
    }, [selectedConversation, api]);

    // Poll for messages
    useEffect(() => {
        if (!selectedConversation) return;
        const pollForMessages = async () => {
            try {
                const response = await api.get(`/api/messaging/GetMessages?conversationId=${selectedConversation}`);
                setMessages(response.data.messages || []);
            } catch (err) {
                console.error('Error polling for messages:', err);
            }
        };
        const interval = setInterval(pollForMessages, 3000);
        return () => clearInterval(interval);
    }, [selectedConversation, api]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((newMessage.length > 0 || uploadedFiles.length > 0) && selectedConversation && currentUserId) {
            try {
                setErrorMessage(null);
                const formData = new FormData();
                formData.append('conversationId', selectedConversation);
                formData.append('senderId', currentUserId.toString());
                if (newMessage.length > 0) {
                    formData.append('content', newMessage);
                }
                uploadedFiles.forEach((file) => {
                    formData.append('attachments', file);
                });
                await api.post('/api/messaging/SendMessage', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const response = await api.get(`/api/messaging/GetMessages?conversationId=${selectedConversation}`);
                setMessages(response.data.messages || []);
                const conversationsResponse = await api.get('/api/messaging/getclientconversations');
                setConversations(conversationsResponse.data.conversations || []);
                setNewMessage('');
                setUploadedFiles([]);
            } catch (err) {
                console.error('Error sending message:', err);
                setErrorMessage('Failed to send message');
            }
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setUploadedFiles(prev => [...prev, ...Array.from(files)]);
            setTimeout(() => {
                e.target.value = '';
            }, 0);
        }
    };

    // Handle starting a new conversation (with family selection)
    const handleStartConversation = async (familyId: number) => {
        if (!familyId) {
            setErrorMessage('Please select a family.');
            return;
        }
        try {
            setErrorMessage(null);
            const response = await api.post('/api/messaging/CreateClientConversation', {
                familyId: familyId
            });
            const newConversation: Conversation = response.data.conversation;
            const conversationsResponse = await api.get('/api/messaging/getclientconversations');
            setConversations(conversationsResponse.data.conversations || []);
            setSelectedConversation(newConversation.id.toString());
            const messagesResponse = await api.get(`/api/messaging/GetMessages?conversationId=${newConversation.id}`);
            setMessages(messagesResponse.data.messages || []);
            setShowFamilyPicker(false);
            setSelectedFamilyId(""); // reset family picker
            dagToast('New conversation started', 'success');
        } catch (err) {
            console.error('Error creating conversation:', err);
            setErrorMessage('Failed to create conversation');
        }
    };

    return (
        <>
        <AuthRedirect />
        <div className="max-w-7xl mx-auto pt-0 pb-4 px-4 lg:p-6">
            {/* Header / masthead */}
            <header className="dag-msg-hero mb-3 lg:mb-4">
                <div className="dag-msg-hero__lead">
                    <p className="dag-msg-hero__eyebrow">Digital Family Office</p>
                    <h1 className="dag-msg-hero__title">Messages</h1>
                    <p className="dag-msg-hero__sub">Communicate with your DFO team</p>
                </div>
                <button
                    type="button"
                    className="dag-msg-hero__new hidden lg:inline-flex"
                    onClick={() => setShowFamilyPicker(true)}
                >
                    <PlusIcon className="w-5 h-5" />
                    <span>New Message</span>
                </button>
            </header>

            {errorMessage && (
                <div className="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l-1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800">{errorMessage}</p>
                        </div>
                        <div className="ml-auto pl-3">
                            <button
                                onClick={() => setErrorMessage(null)}
                                className="text-red-400 hover:text-red-600"
                            >
                                <span className="sr-only">Dismiss</span>
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div
                className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6"
                style={{ height: getResponsiveHeight(isMobile) }}
            >
                {/* Sidebar */}
                <div className={`bg-white rounded-xl shadow-md flex flex-col h-full min-h-0 ${selectedConversation && isMobile ? 'hidden' : 'flex'}`}>
                    <div className="p-4 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Conversations</h3>
                        <button
                            className="flex lg:hidden dfo-bg-gradient-green text-white pl-2 pr-1 py-1 items-center space-x-1 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
                            onClick={() => {
                                setShowFamilyPicker(true);
                            }}
                        >
                            <span>New</span>
                            <PlusIcon className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {loading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="text-gray-500">Loading conversations...</div>
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="text-gray-500">No conversations yet</div>
                            </div>
                        ) : (
                            conversations.map((conversation) => {
                                const familyName = families.find(fam => fam.id === conversation.familyId)?.name ?? "";
                                return (
                                    <div
                                        key={conversation.id}
                                        onClick={async () => {
                                            // If clicking the same conversation that's already selected, deselect it
                                            if (selectedConversation === conversation.id.toString()) {
                                                setSelectedConversation(null);
                                            } else {
                                                setSelectedConversation(conversation.id.toString());
                                                if (isMobile) window.scrollTo(0, 0);
                                                try {
                                                    await api.post(`/api/messaging/MarkMessagesAsRead?conversationId=${conversation.id}`);
                                                    const conversationsResponse = await api.get('/api/messaging/getclientconversations');
                                                    setConversations(conversationsResponse.data.conversations || []);
                                                } catch (err) {
                                                    console.error('Error marking messages as read:', err);
                                                }
                                            }
                                        }}
                                        className={`dag-conv-item p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors relative ${selectedConversation === conversation.id.toString()
                                            ? 'dag-conv-selected'
                                            : ''
                                            } ${conversation.unreadCount > 0 ? 'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-teal-500 before:to-teal-600 before:content-[""]' : ''}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                                                <div className="dag-conv-avatar w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <UserCircleIcon className="w-6 h-6 text-gray-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <h3 className={`text-sm font-semibold truncate ${conversation.unreadCount > 0 ? 'text-gray-900 font-bold' : 'text-gray-900'}`}>
                                                            {(conversation.otherParticipantName && conversation.otherParticipantName !== "Unknown Concierge")
                                                                ? conversation.otherParticipantName
                                                                : "Unassigned Concierge"}
                                                        </h3>
                                                    </div>
                                                    {conversation.otherParticipantEmail && (
                                                        <p className="text-xs text-gray-500 truncate">
                                                            {conversation.otherParticipantEmail}
                                                        </p>
                                                    )}
                                                    {familyName && (
                                                        <p className="text-xs text-gray-400 truncate">
                                                            Family: {familyName}
                                                        </p>
                                                    )}
                                                    {conversation.lastMessageContent && (
                                                        <p className={`text-sm truncate mt-1 ${conversation.unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-600'
                                                            }`}>
                                                            {conversation.lastMessageContent}
                                                        </p>
                                                    )}
                                                    {conversation.lastMessageDate && (
                                                        <p
                                                            className="text-xs text-gray-500 mt-1"
                                                            title={formatTimestamp(conversation.lastMessageDate)}
                                                        >
                                                            {formatRelativeTime(conversation.lastMessageDate)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div className={`lg:col-span-2 flex flex-col h-full min-h-0 bg-white rounded-xl shadow-md overflow-hidden ${selectedConversation || !isMobile ? 'flex' : 'hidden lg:flex'}`}>
                    {selectedConversation && isMobile && (
                        <div className="p-4 border-b border-gray-200 flex-shrink-0 flex items-center">
                            <button
                                onClick={() => setSelectedConversation(null)}
                                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors mr-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="w-6 h-6 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                                <UserCircleIcon className="w-4 h-4 text-gray-600" />
                            </div>
                            <h2 className="text-lg font-semibold text-gray-900 ml-2 truncate">{selectedConv?.otherParticipantName || `Conversation ${selectedConv?.id}`}</h2>
                        </div>
                    )}
                    <div className="flex-1 min-h-0 flex flex-col">
                        <ChatWindow
                            selectedConv={selectedConv}
                            messages={messages}
                            messagesLoading={messagesLoading}
                            currentUserId={currentUserId}
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            handleSendMessage={handleSendMessage}
                            handleFileUpload={handleFileUpload}
                            uploadedFiles={uploadedFiles}
                            setUploadedFiles={setUploadedFiles}
                            isMobile={isMobile}
                            formatTimestamp={formatTimestamp}
                            groupMessagesByDate={groupMessagesByDate}
                            formatDateHeader={formatDateHeader}
                            showHeader={!isMobile}
                        />
                    </div>
                </div>
            </div>

            {/* Family Picker Modal */}
            {showFamilyPicker && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Select Family</h3>
                                <button
                                    onClick={() => setShowFamilyPicker(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="mb-4 relative">
                                <FormSelect
                                    label="Family"
                                    btnTestId="family-dropdown"
                                    placeholder="Pick a family"
                                    value={selectedFamilyId === "" ? null : selectedFamilyId}
                                    options={families.map(fam => ({ value: fam.id, label: fam.name }))}
                                    onChange={(val) => setSelectedFamilyId(val === null ? "" : (val as number))}
                                />
                            </div>
                            <button
                                type="button"
                                className="w-full mt-4 px-4 py-2 rounded-md font-semibold disabled:cursor-not-allowed btn-primary"
                                disabled={!selectedFamilyId}
                                onClick={() => handleStartConversation(selectedFamilyId as number)}
                            >
                                Create Message
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default MessagingPage;